import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useAuth, useSignIn } from '@clerk/react';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@design-system';
import { AlertCircle, ArrowLeft, Loader2, LogIn, Mail, Phone } from 'lucide-react';
import { WaterlilyLogo } from '@client/components/WaterlilyLogo';
import {
  SUPPORTED_COUNTRIES,
  clerkMessage,
  formatByCountry,
  stripDigits,
  validateForCountry,
  type SupportedCountryCode,
} from '@client/util/clerkAuthHelpers';

const phoneSchema = z
  .object({
    countryCode: z.enum(['+1', '+91']),
    phoneNumber: z.string().min(1, 'Phone number is required'),
  })
  .superRefine((val, ctx) => {
    const err = validateForCountry(stripDigits(val.phoneNumber), val.countryCode);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phoneNumber'], message: err });
  });
type PhoneFormValues = z.infer<typeof phoneSchema>;

const emailSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .toLowerCase()
    .email('Please enter a valid email address'),
});
type EmailFormValues = z.infer<typeof emailSchema>;

type Step = 'start' | 'verify_phone' | 'check_email';

export function Login() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirectTo = searchParams.get('redirect_to') || '/';
  const emailHint = searchParams.get('email');

  const { signIn, errors: clerkErrors } = useSignIn();
  const { isSignedIn } = useAuth();

  const [step, setStep] = useState<Step>('start');
  const [mode, setMode] = useState<'phone' | 'email'>('email');
  const [phoneCode, setPhoneCode] = useState('');
  const [emailAddress, setEmailAddress] = useState<string | null>(null);
  const [phoneNumberDisplay, setPhoneNumberDisplay] = useState<string | null>(null);
  const [emailLinkVerified, setEmailLinkVerified] = useState(false);

  useEffect(() => {
    if (!isSignedIn) return;
    if (redirectTo.startsWith('http')) {
      window.location.href = redirectTo;
    } else {
      navigate(redirectTo, { replace: true });
    }
  }, [isSignedIn, redirectTo, navigate]);

  useEffect(() => {
    if (step !== 'check_email') return;
    let cancelled = false;
    void signIn.emailLink.waitForVerification().then(({ error }) => {
      if (!cancelled && !error) setEmailLinkVerified(true);
    });
    return () => {
      cancelled = true;
    };
    // Intentionally depend only on `step`. `signIn` is a new object reference on
    // every signal update — including it would re-fire the long-poll on every
    // render and trigger Clerk's 429 rate limit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    mode: 'onChange',
    defaultValues: { countryCode: '+1', phoneNumber: '' },
  });

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    mode: 'onChange',
    defaultValues: { email: emailHint ?? '' },
  });

  const sendPhoneMutation = useMutation({
    mutationFn: async (values: PhoneFormValues) => {
      const e164 = `${values.countryCode}${stripDigits(values.phoneNumber)}`;
      const { error } = await signIn.phoneCode.sendCode({ phoneNumber: e164 });
      if (error) throw error;
      setPhoneNumberDisplay(e164);
    },
    onSuccess: () => setStep('verify_phone'),
  });

  const verifyPhoneMutation = useMutation({
    mutationFn: async (code: string) => {
      const { error: verifyError } = await signIn.phoneCode.verifyCode({ code });
      if (verifyError) throw verifyError;
      const { error: finalizeError } = await signIn.finalize();
      if (finalizeError) throw finalizeError;
    },
  });

  const sendEmailLinkMutation = useMutation({
    mutationFn: async (values: EmailFormValues) => {
      const { error } = await signIn.emailLink.sendLink({
        emailAddress: values.email,
        verificationUrl: `${window.location.origin}/`,
      });
      if (error) throw error;
      setEmailAddress(values.email);
    },
    onSuccess: () => setStep('check_email'),
  });

  const resendPhoneMutation = useMutation({
    mutationFn: async () => {
      const { error } = await signIn.phoneCode.sendCode();
      if (error) throw error;
    },
    onSuccess: () => {
      setPhoneCode('');
      verifyPhoneMutation.reset();
    },
  });

  const resendLinkMutation = useMutation({
    mutationFn: async () => {
      if (!emailAddress) return;
      const { error } = await signIn.emailLink.sendLink({
        emailAddress,
        verificationUrl: `${window.location.origin}/`,
      });
      if (error) throw error;
    },
  });

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = stripDigits(e.target.value);
    phoneForm.setValue('phoneNumber', formatByCountry(digits, phoneForm.getValues('countryCode')), {
      shouldValidate: true,
    });
  };

  const handleCountryCodeChange = (code: SupportedCountryCode) => {
    phoneForm.setValue('countryCode', code, { shouldValidate: true });
    const digits = stripDigits(phoneForm.getValues('phoneNumber'));
    phoneForm.setValue('phoneNumber', formatByCountry(digits, code), { shouldValidate: true });
  };

  const goBack = async () => {
    setPhoneCode('');
    setEmailLinkVerified(false);
    await signIn.reset();
    verifyPhoneMutation.reset();
    sendPhoneMutation.reset();
    sendEmailLinkMutation.reset();
    setStep('start');
  };

  const isSigningIn =
    isSignedIn === true ||
    searchParams.has('__clerk_status') ||
    searchParams.has('__clerk_created_session');

  const mutationError =
    clerkMessage(verifyPhoneMutation.error as { longMessage?: string; message?: string } | null) ??
    clerkMessage(sendPhoneMutation.error as { longMessage?: string; message?: string } | null) ??
    clerkMessage(sendEmailLinkMutation.error as { longMessage?: string; message?: string } | null);

  const hasInlineFieldError = Object.values(clerkErrors.fields).some((f) => f != null);

  const displayError = hasInlineFieldError
    ? null
    : (mutationError ??
      (clerkErrors.global && clerkErrors.global.length > 0
        ? clerkMessage(clerkErrors.global[0])
        : null));

  if (isSigningIn) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <Spinner className="size-8 text-primary" />
          <p className="text-sm text-gray-700">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {verifyPhoneMutation.isPending && (
        <div className="fixed inset-0 z-100 flex items-center justify-center bg-white/70">
          <Spinner className="size-8 text-primary" />
        </div>
      )}
      <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b bg-white px-4 py-4 md:px-6">
        <WaterlilyLogo className="h-6" />
        {!emailLinkVerified && (
          <Link
            to="/signup"
            className="text-sm text-gray-600 transition-colors duration-200 hover:text-gray-800"
          >
            Don't have an account?{' '}
            <span className="text-primary font-medium">Sign up</span>
          </Link>
        )}
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-4 pb-12 pt-18 md:pt-20">
        <div className="w-full max-w-md">
          <Card className="overflow-hidden border bg-background p-0 gap-0 rounded-2xl shadow-none">
            <div className="p-1">
              <div className="rounded-xl bg-muted px-4 py-8 text-center">
                <div className="mb-2 flex items-center justify-center gap-2">
                  {step === 'start' && <LogIn className="size-5 text-primary-dark" />}
                  {step === 'verify_phone' && <Phone className="size-5 text-primary-dark" />}
                  {step === 'check_email' && <Mail className="size-5 text-primary-dark" />}
                  <span className="text-xs font-semibold uppercase tracking-widest text-primary-dark">
                    {step === 'start' && 'Sign In'}
                    {step === 'verify_phone' && 'Verify Your Phone'}
                    {step === 'check_email' && 'Check Your Email'}
                  </span>
                </div>
                <h1 className="text-xl font-bold text-gray-950">
                  {step === 'start' && 'Welcome back'}
                  {step === 'verify_phone' && 'Enter the text code'}
                  {step === 'check_email' && 'Login link sent to your email'}
                </h1>
                <p className="mx-auto mt-1 max-w-xs text-sm text-foreground">
                  {step === 'start' && 'Sign in with a phone code or an email magic link'}
                  {step === 'verify_phone' && 'We just sent a 6-digit code to your phone'}
                  {step === 'check_email' &&
                    'Click the link we just emailed to finish signing in. You can close this tab.'}
                </p>
              </div>
            </div>
            <CardContent className="p-6">
              {step === 'start' && (
                <Tabs value={mode} onValueChange={(v) => setMode(v as 'phone' | 'email')}>
                  <TabsList className="mb-4 grid w-full grid-cols-2">
                    <TabsTrigger value="email">
                      <Mail className="mr-1 size-4" />
                      Email
                    </TabsTrigger>
                    <TabsTrigger value="phone">
                      <Phone className="mr-1 size-4" />
                      Phone
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="phone">
                    <Form {...phoneForm}>
                      <form
                        onSubmit={phoneForm.handleSubmit((v) => sendPhoneMutation.mutate(v))}
                        className="space-y-4"
                      >
                        <FormField
                          control={phoneForm.control}
                          name="phoneNumber"
                          render={({ field }) => {
                            const selected =
                              SUPPORTED_COUNTRIES.find(
                                (c) => c.code === phoneForm.watch('countryCode'),
                              ) ?? SUPPORTED_COUNTRIES[0];
                            return (
                              <FormItem>
                                <FormLabel className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                  <Phone className="h-4 w-4 text-gray-400" />
                                  Phone number
                                </FormLabel>
                                <FormControl>
                                  <div className="flex gap-2">
                                    <Select
                                      value={selected.code}
                                      onValueChange={(v) =>
                                        handleCountryCodeChange(v as SupportedCountryCode)
                                      }
                                    >
                                      <SelectTrigger
                                        className="focus:border-primary focus:ring-primary h-12! w-30 rounded-lg border-gray-200"
                                        aria-label="Country code"
                                      >
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {SUPPORTED_COUNTRIES.map((c) => (
                                          <SelectItem key={c.code} value={c.code}>
                                            <span className="mr-1">{c.flag}</span>
                                            {c.code}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Input
                                      type="tel"
                                      placeholder={selected.placeholder}
                                      className="focus:border-primary focus:ring-primary h-12 flex-1 rounded-lg border-gray-200 transition-all duration-200"
                                      autoFocus
                                      {...field}
                                      onChange={handlePhoneNumberChange}
                                    />
                                  </div>
                                </FormControl>
                                <FormMessage />
                                {clerkErrors.fields.identifier && (
                                  <p className="text-sm text-destructive">
                                    {clerkMessage(clerkErrors.fields.identifier)}
                                  </p>
                                )}
                              </FormItem>
                            );
                          }}
                        />
                        {displayError && (
                          <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{displayError}</AlertDescription>
                          </Alert>
                        )}
                        <Button
                          type="submit"
                          className="w-full py-6"
                          disabled={!phoneForm.formState.isValid || sendPhoneMutation.isPending}
                        >
                          {sendPhoneMutation.isPending && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          Send phone code
                        </Button>
                      </form>
                    </Form>
                  </TabsContent>

                  <TabsContent value="email">
                    <Form {...emailForm}>
                      <form
                        onSubmit={emailForm.handleSubmit((v) => sendEmailLinkMutation.mutate(v))}
                        className="space-y-4"
                      >
                        <FormField
                          control={emailForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <Mail className="h-4 w-4 text-gray-400" />
                                Email address
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="you@example.com"
                                  className="focus:border-primary focus:ring-primary h-12 rounded-lg border-gray-200 transition-all duration-200"
                                  autoFocus
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                              {clerkErrors.fields.identifier && (
                                <p className="text-sm text-destructive">
                                  {clerkMessage(clerkErrors.fields.identifier)}
                                </p>
                              )}
                            </FormItem>
                          )}
                        />
                        {displayError && (
                          <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{displayError}</AlertDescription>
                          </Alert>
                        )}
                        <Button
                          type="submit"
                          className="w-full py-6"
                          disabled={!emailForm.formState.isValid || sendEmailLinkMutation.isPending}
                        >
                          {sendEmailLinkMutation.isPending && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          Send login link
                        </Button>
                      </form>
                    </Form>
                  </TabsContent>
                </Tabs>
              )}

              {step === 'verify_phone' && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (phoneCode.length === 6) verifyPhoneMutation.mutate(phoneCode);
                  }}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <Phone className="h-4 w-4 text-gray-400" />
                      Phone code
                    </label>
                    <p className="text-xs text-gray-600">
                      Sent to{' '}
                      <span className="font-medium text-gray-800">{phoneNumberDisplay}</span>
                    </p>
                    <InputOTP
                      maxLength={6}
                      value={phoneCode}
                      onChange={setPhoneCode}
                      autoFocus
                      containerClassName="w-full gap-3"
                    >
                      <InputOTPGroup className="flex-1">
                        {[0, 1, 2].map((i) => (
                          <InputOTPSlot key={i} index={i} className="h-12 flex-1 text-base" />
                        ))}
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup className="flex-1">
                        {[3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} className="h-12 flex-1 text-base" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                    <button
                      type="button"
                      className="text-primary hover:text-primary-hover text-xs font-medium"
                      onClick={() => resendPhoneMutation.mutate()}
                    >
                      Resend text code
                    </button>
                  </div>

                  {displayError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{displayError}</AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    className="w-full py-6"
                    disabled={verifyPhoneMutation.isPending}
                  >
                    {verifyPhoneMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Sign in
                  </Button>

                  <button
                    type="button"
                    className="flex w-full items-center justify-center gap-1 text-sm text-gray-600 transition-colors hover:text-gray-800"
                    onClick={goBack}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Use a different method
                  </button>
                </form>
              )}

              {step === 'check_email' && (
                <div className="space-y-6">
                  <div className="flex flex-col items-center gap-3 rounded-xl bg-primary/5 p-6 text-center">
                    <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
                      <Mail className="size-6 text-primary" />
                    </div>
                    <p className="text-sm text-gray-700">We sent a login link to</p>
                    <p className="text-base font-medium text-gray-950">{emailAddress}</p>
                    <p className="text-xs text-gray-600">
                      Open your inbox and click the link to finish signing in. The link expires in
                      10 minutes.
                    </p>
                  </div>

                  {emailLinkVerified ? (
                    <Alert className="border-primary/30 bg-primary/5">
                      <AlertDescription className="text-primary-dark">
                        You're signed in on the tab that opened the link. You can close this page.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                      <Spinner className="size-4 text-primary" />
                      Waiting for you to click the link…
                    </div>
                  )}

                  {displayError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{displayError}</AlertDescription>
                    </Alert>
                  )}

                  {!emailLinkVerified && (
                    <>
                      <button
                        type="button"
                        className="text-primary hover:text-primary-hover block w-full text-sm font-medium"
                        onClick={() => resendLinkMutation.mutate()}
                      >
                        Resend login link
                      </button>

                      <button
                        type="button"
                        className="flex w-full items-center justify-center gap-1 text-sm text-gray-600 transition-colors hover:text-gray-800"
                        onClick={goBack}
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Use a different method
                      </button>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
